import { Button } from '@/components/ui/shadcn/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/shadcn/form'
import { Input } from '@/components/ui/shadcn/input'
import { trpc } from '@/config/trpc'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Please enter a valid email address' }),
})

export function AuthVerificationError({ message }: { message: string }) {
  const [isSent, setIsSent] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  })

  const { mutateAsync: resendVerifyToken, isPending } =
    trpc.auth.resendVerificationToken.useMutation({
      onError: (error) =>
        form.setError('root', {
          message: error.message,
        }),
    })

  const handleResend = async (values: z.infer<typeof formSchema>) => {
    if (isPending) return

    await resendVerifyToken({ email: values.email })
    setIsSent(true)
  }

  return (
    <div>
      {isSent ? (
        <p className="text-center text-sm sm:text-base">
          Verification email sent! Please check your inbox.
        </p>
      ) : isPending ? (
        <div className="flex items-center justify-center">
          <div className="border-primary/30 border-t-primary h-6 w-6 animate-spin rounded-full border-4"></div>
        </div>
      ) : (
        <>
          <div className="text-destructive text-center">
            <p className="text-sm sm:text-base">Verification failed</p>
            <p className="text-sm sm:text-base">{message}</p>
          </div>
          <p className="py-2 text-center text-xs sm:text-sm">
            Please enter your email address and click "Resend" to receive a new verification email.
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleResend)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" size="lg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <div className="mt-1">
                  <FormMessage>{form.formState.errors.root.message}</FormMessage>
                </div>
              )}

              <Button type="submit" disabled={isPending} className="mt-4 w-full" size="lg">
                {isPending ? (
                  <span className="border-primary/30 border-t-primary inline-block h-4 w-4 animate-spin rounded-full border-2" />
                ) : (
                  'Resend'
                )}
              </Button>
            </form>
          </Form>
        </>
      )}
    </div>
  )
}
